import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import UserService from "rc-app/services/UserService";
import { getClientIdByClientManger } from "rc-app/api/agencyActionsApi";
import { getAllAgent } from "rc-app/api/agentActionsApi";
import { getCustomerList } from "rc-app/api/customerActionsApi";
import {
  getSystemAdminList,
  createClientAdmin,
} from "rc-app/api/systemBusinessAdminActionApi";
import Icon from "rc-app/components/Icons";
import { AddButton } from "rc-app/components/Buttons";
import { toTitleCase } from "rc-app/utils";
import PersonFormModal from "rc-app/components/PersonFormModal";

/**
 * System Admins (ASA) — ABA-only screen (gated in ClientHomePage nav).
 * Per the role matrix the ABA gets list / create / edit here. This ships list
 * + create (same row UI as Agents/Leads); edit reuses editClientAdmin later.
 * The ABA assigns agents to an ASA from the Agents tab (agent.asa_id).
 */
interface SystemAdmin {
  id: string;
  // ASA records store a single `name` (full name), not first/last — see the
  // super_admin Edit modal which loads `agency_system_admin.name`.
  name: string;
  email: string;
  phone: string;
  status: "Active" | "Inactive";
  photo?: string;
  createdAt?: string;
  agentCount: number;
  leadCount: number;
}

const SystemAdmins: React.FC = () => {
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();
  const [admins, setAdmins] = useState<SystemAdmin[]>([]);
  const [editing, setEditing] = useState<SystemAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showCreate, setShowCreate] = useState(false);

  // Needed to create an ASA scoped to this ABA's agency.
  const [agencyId, setAgencyId] = useState<string>("");
  const [abaId, setAbaId] = useState<string>("");

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      // ABA-only screen — resolve the agency via the business-admin profile,
      // then list the agency's ASAs.
      const profile: any = await getClientIdByClientManger(
        UserService.getUseremail(),
      );
      const aid = profile?.agency_business_admin?.agency_id;
      // The create endpoint wants the BA's `agency_business_admin_id` field
      // (what super_admin passes), NOT the Mongo `_id`. Fall back just in case.
      const bid =
        profile?.agency_business_admin?.agency_business_admin_id ||
        profile?.agency_business_admin?._id ||
        profile?.agency_business_admin?.id;
      setAgencyId(aid || "");
      setAbaId(bid || "");
      if (!aid) {
        setAdmins([]);
        setLoading(false);
        return;
      }

      // ASA list + agents + customers (the latter two power the per-ASA
      // Agents/Leads counts). Agents/customers are best-effort.
      const [res, agentsRes, custRes] = await Promise.all([
        getSystemAdminList(aid),
        getAllAgent(0, 10000, aid).catch(() => null),
        getCustomerList(0, 10000, aid).catch(() => null),
      ]);

      // Leads per agent (Mongo id): a customer's agent_id is an array of
      // assigned agent ids.
      const leadPerAgent: Record<string, number> = {};
      ((custRes as any)?.re_customer_list || []).forEach((c: any) => {
        (c.agent_id || []).forEach((id: string) => {
          if (id && id.trim())
            leadPerAgent[id] = (leadPerAgent[id] || 0) + 1;
        });
      });

      // Roll agents (and their leads) up to the ASA they're assigned to.
      // asa_id is pending (Nivedita) — until it lands these stay 0.
      const perAsa: Record<string, { agents: number; leads: number }> = {};
      ((agentsRes as any)?.re_agent_list || []).forEach((ag: any) => {
        const raw = ag.assigned_asa_id || ag.asa_id;
        const asaId = raw ? String(Array.isArray(raw) ? raw[0] : raw) : "";
        if (!asaId) return;
        if (!perAsa[asaId]) perAsa[asaId] = { agents: 0, leads: 0 };
        perAsa[asaId].agents += 1;
        perAsa[asaId].leads += leadPerAgent[ag._id] || 0;
      });

      const list = (res as any)?.agency_system_admin_list || [];
      const mapped: SystemAdmin[] = list.map((a: any) => ({
        id: a._id,
        name:
          a.name ||
          `${a.first_name || ""} ${a.last_name || ""}`.trim() ||
          "",
        email: a.email_id || "",
        phone: a.mobile_number ? `${a.mobile_number}` : "",
        status: a.is_active ? "Active" : "Inactive",
        photo: a.profile_pic,
        createdAt: a.created_at || a.createdAt || "",
        agentCount: perAsa[a._id]?.agents || 0,
        leadCount: perAsa[a._id]?.leads || 0,
      }));
      setAdmins(mapped);
    } catch (_e) {
      setAdmins([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const displayName = (a: SystemAdmin) => a.name || a.email;

  const initials = (a: SystemAdmin) =>
    a.name
      .split(" ")
      .map((w) => w[0])
      .filter(Boolean)
      .slice(0, 2)
      .join("")
      .toUpperCase() || "SA";

  const fmtDate = (iso?: string) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const filtered = admins.filter((a) => {
    const q = search.toLowerCase().trim();
    if (!q) return true;
    return (
      displayName(a).toLowerCase().includes(q) ||
      a.email.toLowerCase().includes(q) ||
      a.phone.toLowerCase().includes(q)
    );
  });

  return (
    <div className="font-inter h-full bg-gradient-to-b from-[#1C2C3A] to-[#1E4F52] p-3 sm:p-4 md:p-6">
      {/* Breadcrumb + search + add */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between mb-4 md:mb-6">
        <div className="flex items-center gap-1 text-sm">
          <button
            type="button"
            onClick={() => navigate(slug ? `/c/${slug}` : "/")}
            className="text-[#889DB2] hover:underline"
          >
            Home
          </button>
          <span className="text-[#889DB2] mx-1">›</span>
          <span className="text-white font-medium">
            System Admins ({admins.length})
          </span>
        </div>
        <div className="flex gap-2 sm:gap-4 w-full sm:w-auto">
          <div className="relative flex-1 sm:flex-none">
            <input
              type="text"
              placeholder="Search..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 sm:pl-12 w-full sm:w-64 lg:w-85 px-3 py-2 text-sm sm:text-base text-[#F4F5F7] rounded-md bg-[#2E4051] border-none focus:outline-none focus:ring-2 focus:ring-[#008564] transition-colors placeholder:text-[#889DB2]"
            />
            <Icon
              name="ic-search"
              className="absolute left-3 sm:left-5 top-1/2 -translate-y-1/2 w-4 h-4"
            />
          </div>
          <AddButton
            label="Add System Admin"
            onClick={() => setShowCreate(true)}
            iconClassName="h-3 w-3"
          />
        </div>
      </div>

      {/* Row table */}
      <div className="bg-[#15333D] border border-[#2C4D5A] rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px] border-collapse text-left">
            <thead>
              <tr className="bg-[#11272E] text-[11px] uppercase tracking-wide text-[#7C97A8]">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Email ID</th>
                <th className="px-4 py-3 font-medium">Phone Number</th>
                <th className="px-4 py-3 font-medium text-center">Agents</th>
                <th className="px-4 py-3 font-medium text-center">Leads</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Created</th>
                <th className="px-4 py-3 font-medium text-center">Edit</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <div className="inline-block w-10 h-10 border-4 border-[#34D399] border-t-transparent rounded-full animate-spin" />
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-[#7C97A8]">
                    {search
                      ? "No System Admins match your search."
                      : "No System Admins yet. Add one to get started."}
                  </td>
                </tr>
              ) : (
                filtered.map((a) => (
                  <tr
                    key={a.id}
                    className="border-t border-[#22323D] hover:bg-[#1A2F38] transition-colors"
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#34D399] to-[#059669] flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                          {a.photo ? (
                            <img
                              src={a.photo}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            initials(a)
                          )}
                        </div>
                        <span className="text-sm text-white font-medium whitespace-nowrap">
                          {toTitleCase(displayName(a), " ")}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                      {a.email}
                    </td>
                    <td className="px-4 py-3 text-sm text-[#C7D6DD] whitespace-nowrap">
                      {a.phone || "—"}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex min-w-[24px] justify-center rounded-md bg-white/5 px-2 py-0.5 text-xs text-[#C7D6DD]">
                        {a.agentCount}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="inline-flex min-w-[24px] justify-center rounded-md bg-white/5 px-2 py-0.5 text-xs text-[#C7D6DD]">
                        {a.leadCount}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                          a.status === "Active"
                            ? "bg-[#34D399]/15 text-[#34D399]"
                            : "bg-white/10 text-[#9FB8AF]"
                        }`}
                      >
                        {a.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[#9FB8AF] whitespace-nowrap">
                      {fmtDate(a.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        type="button"
                        onClick={() => setEditing(a)}
                        title="Edit system admin"
                        className="inline-flex items-center justify-center w-8 h-8 rounded-md text-[#9FB8AF] hover:text-white hover:bg-white/5 transition-colors"
                      >
                        <Icon name="ic-edit" className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreate && (
        <PersonFormModal
          role="systemAdmin"
          mode="create"
          agencyId={agencyId}
          abaId={abaId}
          existing={admins.map((a) => ({ email: a.email, phone: a.phone }))}
          onClose={() => setShowCreate(false)}
          onSaved={() => {
            setShowCreate(false);
            fetchAdmins();
          }}
        />
      )}
      {editing && (
        <PersonFormModal
          role="systemAdmin"
          mode="edit"
          agencyId={agencyId}
          record={{
            id: editing.id,
            name: editing.name,
            email: editing.email,
            phone: editing.phone,
          }}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            fetchAdmins();
          }}
        />
      )}
    </div>
  );
};

interface CreateSystemAdminModalProps {
  agencyId: string;
  abaId: string;
  existingAdmins: { email: string; phone: string }[];
  onClose: () => void;
  onCreated: () => void;
}

// Exported so the super-admin flow (MyClientsPage) reuses the exact same modal
// + duplicate-check instead of its own older AddClientAdminModal design.
export const CreateSystemAdminModal: React.FC<CreateSystemAdminModalProps> = ({
  agencyId,
  abaId,
  existingAdmins,
  onClose,
  onCreated,
}) => {
  const [form, setForm] = useState({ name: "", email_id: "", mobile_number: "" });
  const [errors, setErrors] = useState<{ [k: string]: string }>({});
  const [saving, setSaving] = useState(false);

  const validate = () => {
    const e: { [k: string]: string } = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email_id.trim()) e.email_id = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email_id))
      e.email_id = "Enter a valid email address";
    else if (
      existingAdmins.some(
        (a) =>
          a.email.trim().toLowerCase() === form.email_id.trim().toLowerCase(),
      )
    )
      e.email_id = "A System Admin with this email already exists";
    if (!form.mobile_number.trim()) e.mobile_number = "Mobile number is required";
    else if (!/^[6-9]\d{9}$/.test(form.mobile_number))
      e.mobile_number = "Enter a valid 10-digit mobile number";
    else if (
      existingAdmins.some((a) => a.phone.trim() === form.mobile_number.trim())
    )
      e.mobile_number = "A System Admin with this mobile number already exists";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const change = (field: string, value: string) => {
    if (field === "mobile_number") {
      if (value && !/^\d*$/.test(value)) return;
      value = value.slice(0, 10);
    }
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const submit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await createClientAdmin({
        agency_id: agencyId,
        agency_business_admin_id: abaId,
        keycloak_id: "",
        username: "",
        email_id: form.email_id.trim(),
        mobile_number: form.mobile_number.trim(),
        name: form.name.trim(),
      });
      toast.success("System Admin created");
      onCreated();
    } catch (_e) {
      toast.error("Failed to create System Admin");
      setSaving(false);
    }
  };

  const field = (
    label: string,
    key: "name" | "email_id" | "mobile_number",
    placeholder: string,
    type = "text",
  ) => (
    <div>
      <label className="text-[#9FB8AF] text-sm">{label}</label>
      <input
        type={type}
        value={form[key]}
        placeholder={placeholder}
        onChange={(e) => change(key, e.target.value)}
        className={`w-full mt-1 px-3 py-2 rounded-lg bg-[#11272E] text-white text-sm border focus:outline-none focus:ring-2 transition-colors placeholder:text-[#5E7682] ${
          errors[key]
            ? "border-red-500 focus:ring-red-500"
            : "border-[#2C4D5A] focus:ring-[#008564] focus:border-[#008564]"
        }`}
      />
      {errors[key] && (
        <p className="text-red-400 text-xs mt-1">{errors[key]}</p>
      )}
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-3 sm:p-4">
      <div className="bg-[#15333D] border border-[#2C4D5A] rounded-xl w-full max-w-md p-5 sm:p-6">
        <h3 className="text-white text-lg font-semibold">Add System Admin</h3>
        <p className="text-[#9FB8AF] text-sm mt-1">
          They’ll manage agents and leads within your agency.
        </p>

        <div className="mt-5 flex flex-col gap-4">
          {field("Full Name", "name", "e.g. Priya Sharma")}
          {field("Email ID", "email_id", "name@company.com", "email")}
          {field("Phone Number", "mobile_number", "10-digit mobile", "tel")}
        </div>

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-[#C7D6DD] bg-white/5 hover:bg-white/10 transition-colors disabled:opacity-40"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={saving}
            className="px-4 py-2 rounded-lg text-sm text-white bg-[#008564] hover:bg-[#0a9b76] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? "Creating…" : "Create"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SystemAdmins;
